/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Component, Fragment } from 'react'

import { EuiFlexItem, EuiButton, EuiCode, EuiFormHelpText, EuiText, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { ExternalHyperlink } from '@modules/cloud-api/v1/types'
import type { AnyResourceInfo, SliderInstanceType } from '@modules/ui-types'
import { DocLink } from '@modules/doclinks'

import { getResourceVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { getConfigForKey } from '@/store'

import { ResourceExternalLinksList } from './ExternalLinksList'

type Props = {
  resource: AnyResourceInfo
  sliderInstanceType: SliderInstanceType
  hideExternalLinks: boolean
  saasClusterMetrics: boolean
  wrapWithFlexItem?: boolean
}

type State = {
  showingLinks: boolean
}

export default class ResourceExternalLinks extends Component<Props, State> {
  state: State = {
    showingLinks: false,
  }

  render() {
    const { resource, hideExternalLinks, wrapWithFlexItem } = this.props

    if (hideExternalLinks) {
      return null
    }

    const links = getExternalLinks({ resource })

    if (links.length === 0) {
      return null
    }

    const linkList = this.renderExternalLinks()

    if (wrapWithFlexItem) {
      return <EuiFlexItem grow={false}>{linkList}</EuiFlexItem>
    }

    return linkList
  }

  renderExternalLinks() {
    const { resource, sliderInstanceType, saasClusterMetrics } = this.props
    const { showingLinks } = this.state

    let links = getExternalLinks({ resource })
    const version = getResourceVersion({ resource })

    // Add agentless dashboard links for Kibana slider
    if (sliderInstanceType === 'kibana') {
      links = [...links, ...getAgentlessDashboardLinks({ kibanaId: resource.id })]
    }

    const fewLinks = links.length <= 5
    const showLinks = fewLinks || showingLinks

    return (
      <div>
        {externalLinksTitle(saasClusterMetrics)}

        <EuiSpacer size='xs' />

        {showLinks ? (
          <div>
            {fewLinks ? null : (
              <Fragment>
                <EuiSpacer size='xs' />
                {this.renderToggleButton()}
              </Fragment>
            )}

            <ResourceExternalLinksList
              links={links}
              sliderInstanceType={sliderInstanceType}
              version={version}
            />
          </div>
        ) : (
          <div>{this.renderToggleButton()}</div>
        )}
      </div>
    )
  }

  renderToggleButton() {
    const { showingLinks } = this.state

    return (
      <EuiButton size='s' onClick={() => this.setState({ showingLinks: !showingLinks })}>
        {showingLinks ? (
          <FormattedMessage
            id='resource-endpoints.hide-external'
            defaultMessage='Hide dashboard links'
          />
        ) : (
          <FormattedMessage
            id='resource-endpoints.show-external'
            defaultMessage='Show dashboard links'
          />
        )}
      </EuiButton>
    )
  }
}

function externalLinksTitle(saasClusterMetrics?: boolean) {
  return (
    <EuiText size='s'>
      <strong>
        <FormattedMessage
          id='resource-endpoints.external-links'
          defaultMessage='External Links {link}'
          values={{
            link: (
              <div
                css={css({
                  display: 'inline-flex',
                })}
              >
                {saasClusterMetrics ? null : (
                  <EuiFormHelpText>
                    {' - '}

                    <FormattedMessage
                      id='cluster-logging-endpoint.description'
                      defaultMessage='Log in with {elastic} user. {dontKnowPassword}'
                      values={{
                        elastic: <EuiCode>elastic</EuiCode>,
                        dontKnowPassword: (
                          <DocLink link='monitoringEceDocLink'>
                            <FormattedMessage
                              id='cluster-logging-endpoint.dont-know-password'
                              defaultMessage='Forgot the password?'
                            />
                          </DocLink>
                        ),
                      }}
                    />
                  </EuiFormHelpText>
                )}
              </div>
            ),
          }}
        />
      </strong>
    </EuiText>
  )
}

function getExternalLinks({ resource }: { resource?: AnyResourceInfo }): ExternalHyperlink[] {
  if (!resource) {
    return []
  }

  return resource.info.external_links
}

function getAgentlessDashboardLinks({ kibanaId }: { kibanaId: string }): ExternalHyperlink[] {
  const o11yDomain = getConfigForKey(`CLOUD_SERVERLESS_O11Y_DOMAIN`)

  if (!o11yDomain) {
    return []
  }

  // Agentless logs uses the serverless project applications logs dashboard filtered by application id and kibana component id
  const agentlessLogsUrl = `https://${o11yDomain}/app/dashboards#/view/serverless-project-applications-logs?_g=(filters:!((query:(match_phrase:(kubernetes.labels.k8s_elastic_co/application-id:agentless))),(query:(match_phrase:(kubernetes.labels.k8s_elastic_co/kibana-component-id:'${kibanaId}')))))`

  // Agentless metrics uses its own dashboard filtered by kibana component id
  const agentlessMetricsUrl = `https://${o11yDomain}/app/dashboards#/view/agentless-kubernetes-pods-dashboard-id?_g=(filters:!((query:(match_phrase:(kubernetes.labels.k8s_elastic_co/kibana-component-id:'${kibanaId}')))))`

  return [
    {
      id: 'agentless-logs',
      label: 'Agentless logs',
      uri: agentlessLogsUrl,
    },
    {
      id: 'agentless-metrics',
      label: 'Agentless metrics',
      uri: agentlessMetricsUrl,
    },
  ]
}
